// The "Only when…" section of the custom alarm builder (custom + conditional
// alarms, phase 6). Up to four condition rows, an all/any toggle once there are
// two, and a permanent note about what the boat does when a condition's signal
// goes quiet.
//
// Closed and empty by default. Most alarms are unconditional, so the section
// starts as one optional-looking button and only becomes a form once the
// operator asks for it — and removing the last row folds it back, so the page
// never carries an empty scaffold.
//
// Each row reuses `SignalPicker` unchanged (its full-screen phone sheet comes
// along for free), narrowed to the signals a clause can actually be made from:
// numbers compare against a value, switches are on or off, and everything else
// has no clause type on the wire at all. Offering a text signal and then
// refusing every operator for it would read as a broken form.
//
// The live chip per row is three-way — "true now" / "false now" / "no reading" —
// because those are three different facts. The boat reads a clause with no data
// as FALSE (fail-closed), which under "all" silences the whole alarm; the chip
// says only what we can see, and the note under the rows says what the boat
// does. Neither pretends to be the other.
//
// This component owns no state. The draft, the verdicts and the errors are all
// computed by the page from the pure modules, so what the rows show and what the
// review sentence says come from one calculation.

import {
  clauseChip,
  conditionOperatorLabel,
  failClosedNote,
} from '../../lib/customAlarmConditions';
import {
  MAX_CONDITIONS,
  conditionOperatorsFor,
  type ConditionDraft,
  type ConditionLogic,
  type ConditionOperator,
} from '../../lib/customAlarmForm';
import { signalDisplayName, type Signal } from '../../lib/signalCatalog';
import { SignalPicker } from './SignalPicker';

export interface ConditionBuilderProps {
  /** The signals a clause can use — `conditionSignals(catalog)` at the call site. */
  signals: readonly Signal[];
  rows: readonly ConditionDraft[];
  logic: ConditionLogic;
  /** `clauseHolds` per row, in row order: true / false / cannot say. */
  verdicts: readonly (boolean | null)[];
  /**
   * Each row signal's reading, already worded by the page — "now 420 rpm", or
   * "420 rpm, 2 hr ago" when it is too old to judge the clause on. Null when the
   * signal has never reported.
   */
  readingHints: readonly (string | null)[];
  /** Each row's validation message, in row order. */
  errors: readonly (string | undefined)[];
  /** A rejection aimed at the section rather than a row. */
  sectionError?: string;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onPickSignal: (index: number, signal: Signal) => void;
  onOperator: (index: number, operator: ConditionOperator) => void;
  onValue: (index: number, value: string) => void;
  onLogic: (logic: ConditionLogic) => void;
}

/** The human name for a row's pinned signal, or undefined while none is pinned. */
function labelFor(signals: readonly Signal[], path: string | undefined): string | undefined {
  if (path === undefined || path.length === 0) return undefined;
  const signal = signals.find((s) => s.path === path);
  if (signal === undefined) return undefined;
  return signalDisplayName(signal);
}

export function ConditionBuilder({
  signals,
  rows,
  logic,
  verdicts,
  readingHints,
  errors,
  sectionError,
  onAdd,
  onRemove,
  onPickSignal,
  onOperator,
  onValue,
  onLogic,
}: ConditionBuilderProps) {
  const atCap = rows.length >= MAX_CONDITIONS;

  return (
    <fieldset className="alarm-form-fieldset alarm-custom-conditions">
      <legend>Only when…</legend>

      {rows.length === 0 ? (
        <>
          <p className="alarm-form-hint">
            Optional. Add a condition and the alarm only counts while that condition holds — low oil
            pressure only while the engine is running, say.
          </p>
          <button
            type="button"
            className="btn-open-sm alarm-custom-condition-add"
            data-testid="alarm-custom-condition-open"
            onClick={onAdd}
          >
            Add a condition
          </button>
        </>
      ) : null}

      {rows.map((row, index) => {
        const signal =
          row.signalPath === undefined ? undefined : signals.find((s) => s.path === row.signalPath);
        const operators = signal === undefined ? [] : conditionOperatorsFor(signal.kind);
        const needsValue = row.operator === 'above' || row.operator === 'below';
        const unit = signal?.unit ?? null;
        const chip = clauseChip(verdicts[index] ?? null);
        const reading = readingHints[index] ?? null;
        const error = errors[index];

        return (
          <div
            key={index}
            className="alarm-custom-condition"
            data-testid={`alarm-custom-condition-row-${index}`}
          >
            <div className="alarm-custom-condition__head">
              <span className="alarm-custom-condition__index">Condition {index + 1}</span>
              <button
                type="button"
                className="alarm-custom-condition__remove"
                data-testid={`alarm-custom-condition-remove-${index}`}
                onClick={() => onRemove(index)}
              >
                Remove
              </button>
            </div>

            <SignalPicker
              signals={signals}
              value={row.signalPath}
              onPick={(picked) => onPickSignal(index, picked)}
              label="Signal"
              testidPrefix={`alarm-custom-condition-signal-${index}`}
            />

            <div className="alarm-custom-condition__test">
              <label className="sr-only" htmlFor={`alarm-custom-condition-op-${index}`}>
                What condition {index + 1} checks
              </label>
              <select
                id={`alarm-custom-condition-op-${index}`}
                className="form-input alarm-custom-condition__op"
                value={row.operator ?? ''}
                disabled={operators.length === 0}
                data-testid={`alarm-custom-condition-op-${index}`}
                onChange={(e) => onOperator(index, e.target.value as ConditionOperator)}
              >
                <option value="" disabled>
                  {signal === undefined ? 'Pick a signal first' : 'Choose…'}
                </option>
                {operators.map((operator) => (
                  <option key={operator} value={operator}>
                    {conditionOperatorLabel(operator)}
                  </option>
                ))}
              </select>

              {needsValue ? (
                <>
                  <label className="sr-only" htmlFor={`alarm-custom-condition-value-${index}`}>
                    Condition {index + 1} value
                  </label>
                  <input
                    id={`alarm-custom-condition-value-${index}`}
                    className="form-input alarm-custom-condition__value"
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={row.value ?? ''}
                    data-testid={`alarm-custom-condition-value-${index}`}
                    onChange={(e) => onValue(index, e.target.value)}
                  />
                  {unit === null || unit === '' ? null : (
                    <span className="alarm-form-unit">{unit}</span>
                  )}
                </>
              ) : null}

              {reading === null ? null : (
                <span
                  className="alarm-custom-now"
                  data-testid={`alarm-custom-condition-now-${index}`}
                >
                  {reading}
                </span>
              )}

              <span
                className={`alarm-chip alarm-custom-chip alarm-custom-chip--${chip.tone}`}
                title={chip.detail}
                data-testid={`alarm-custom-condition-chip-${index}`}
              >
                {chip.text}
                <span className="sr-only">. {chip.detail}</span>
              </span>
            </div>

            {error === undefined ? null : (
              <p className="alarm-form-err" data-testid={`alarm-custom-condition-err-${index}`}>
                {error}
              </p>
            )}
          </div>
        );
      })}

      {rows.length >= 2 ? (
        <div
          className="alarm-custom-condition-logic"
          role="radiogroup"
          aria-label="How these conditions combine"
        >
          <label className="alarm-custom-condition-logic__choice">
            <input
              type="radio"
              name="alarm-custom-condition-logic"
              checked={logic === 'all'}
              data-testid="alarm-custom-condition-logic-all"
              onChange={() => onLogic('all')}
            />
            <span>All of these must be true</span>
          </label>
          <label className="alarm-custom-condition-logic__choice">
            <input
              type="radio"
              name="alarm-custom-condition-logic"
              checked={logic === 'any'}
              data-testid="alarm-custom-condition-logic-any"
              onChange={() => onLogic('any')}
            />
            <span>Any one of these is enough</span>
          </label>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <>
          {atCap ? (
            <p className="alarm-form-hint">
              That is as many conditions as one alarm can carry ({MAX_CONDITIONS}). Remove one to
              add a different condition.
            </p>
          ) : (
            <button
              type="button"
              className="btn-open-sm alarm-custom-condition-add"
              data-testid="alarm-custom-condition-add"
              onClick={onAdd}
            >
              Add another condition
            </button>
          )}

          <p className="alarm-custom-note" data-testid="alarm-custom-condition-failclosed">
            {failClosedNote(
              rows.map((row) => labelFor(signals, row.signalPath)),
              logic
            )}
          </p>
        </>
      ) : null}

      {sectionError === undefined ? null : (
        <p className="alarm-form-err" data-testid="alarm-custom-condition-error">
          {sectionError}
        </p>
      )}
    </fieldset>
  );
}
